import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  Avatar,
  Divider,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const DuplicateBarcodeModal = ({
  open,
  onClose,
  onContinue,
  duplicateItems = [],
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: '20px',
            color: '#0F1214',
          }}
        >
          {t('Duplicate Barcode Warning')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 400,
            color: '#49454F',
            mb: 3,
          }}
        >
          {t('This barcode is already used for the following')}{' '}
          <Typography
            component="span"
            sx={{
              fontSize: '15px',
              fontWeight: 600,
              color: '#49454F',
            }}
          >
            {duplicateItems.length}
          </Typography>{' '}
          {t('items. Are you sure you want to continue?')}
        </Typography>

        {duplicateItems.length > 0 && (
          <Box
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: 1,
            }}
          >
            <List sx={{ p: 0 }}>
              {duplicateItems.map((item, index) => {
                const imageUrl =
                  typeof item.image === 'string'
                    ? item.image
                    : item.image?.url || null;
                const hasImage = Boolean(imageUrl);

                return (
                  <Box key={item._id || index}>
                    <ListItem
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        padding: 1.5,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            fontSize: '15px',
                            color: '#0F1214',
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.name || t('Unnamed Item')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '13px',
                            color: '#49454F',
                            mb: 0.5,
                          }}
                        >
                          {t('Price')}: $
                          {parseFloat(item.price || 0).toFixed(2)}
                        </Typography>
                        {item.duplicateBarcodes &&
                          item.duplicateBarcodes.length > 0 && (
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '12px',
                                  color: '#666',
                                  mr: 0.5,
                                  alignSelf: 'center',
                                }}
                              >
                                {t('Barcode')}:
                              </Typography>
                              {item.duplicateBarcodes.map(
                                (barcode, barcodeIndex) => (
                                  <Chip
                                    key={barcodeIndex}
                                    label={barcode}
                                    size="small"
                                    sx={{
                                      bgcolor: '#fff3cd',
                                      color: '#856404',
                                      border: '1px solid #ffc107',
                                    }}
                                  />
                                )
                              )}
                            </Box>
                          )}
                      </Box>
                      {hasImage && (
                        <Avatar
                          src={imageUrl}
                          alt={item.name}
                          sx={{
                            width: 85,
                            height: 85,
                            borderRadius: 1,
                            bgcolor: '#f5f5f5',
                            flexShrink: 0,
                            ml: 1.5,
                          }}
                          variant="rounded"
                        />
                      )}
                    </ListItem>
                    {index < duplicateItems.length - 1 && <Divider />}
                  </Box>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="outlined"
          sx={{ borderRadius: 1.5, minWidth: 100 }}
        >
          {t('Cancel')}
        </Button>
        <Button
          onClick={onContinue}
          color="primary"
          variant="contained"
          sx={{ borderRadius: 1.5, minWidth: 100, ml: 1 }}
        >
          {t('Continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateBarcodeModal;
